import type { IAutomationProvider } from '../browser/IAutomationProvider'
import { automationEventBus } from '../events'
import { jobQueue, JobQueue } from '../queue/JobQueue'
import { sessionManager, SessionManager } from '../sessions/SessionManager'
import { StubAutomationProvider } from '../strategies/StubAutomationProvider'
import type {
  AutomationConfig,
  AutomationDashboard,
  AutomationFilters,
  AutomationJob,
  AutomationLog,
  AutomationPriority,
  AutomationResult,
  AutomationSession,
  AutomationTask,
} from '../types'
import { DEFAULT_AUTOMATION_CONFIG as DEFAULT_CONFIG } from '../types'
import { workerPool, WorkerPool } from '../workers/WorkerPool'
import {
  automationJobRepository,
  automationLogRepository,
  automationSessionRepository,
} from '../services/repositories'

/**
 * AutomationManager — initializes providers, manages sessions, runs jobs, frees resources.
 */
export class AutomationManager {
  private readonly providers = new Map<string, IAutomationProvider>()
  private config: AutomationConfig = { ...DEFAULT_CONFIG }
  private pumping = false
  private readonly notifications: Array<{ id: string; type: string; message: string; at: string }> = []
  private readonly queue: JobQueue
  private readonly sessions: SessionManager
  private readonly pool: WorkerPool

  constructor(
    queue: JobQueue = jobQueue,
    sessions: SessionManager = sessionManager,
    pool: WorkerPool = workerPool,
  ) {
    this.queue = queue
    this.sessions = sessions
    this.pool = pool
  }

  getConfig(): AutomationConfig {
    return { ...this.config }
  }

  updateConfig(patch: Partial<AutomationConfig>): AutomationConfig {
    this.config = { ...this.config, ...patch }
    this.pool.configure({
      maxWorkers: this.config.maxWorkers,
      timeoutMs: this.config.timeoutMs,
    })
    this.sessions.setTtl(this.config.sessionTtlMs)
    return this.getConfig()
  }

  registerProvider(provider: IAutomationProvider): void {
    this.providers.set(provider.supplierCode, provider)
  }

  ensureProvider(supplierCode: string): IAutomationProvider {
    const existing = this.providers.get(supplierCode)
    if (existing) return existing
    const stub = new StubAutomationProvider(supplierCode)
    this.providers.set(supplierCode, stub)
    return stub
  }

  async initializeProvider(supplierCode: string): Promise<void> {
    const provider = this.ensureProvider(supplierCode)
    await provider.initialize()
  }

  enqueue(input: {
    supplier: string
    operation: string
    priority?: AutomationPriority
    payload?: Record<string, unknown>
  }): AutomationTask {
    const task = this.queue.enqueue({
      supplier: input.supplier,
      operation: input.operation,
      priority: input.priority ?? 3,
      payload: input.payload,
      maxAttempts: this.config.maxAttempts,
    })
    automationJobRepository.upsertFromTask(task)
    this.notify('start', `Automação enfileirada: ${task.supplier}/${task.operation}`)
    void this.pump()
    return task
  }

  cancel(taskId: string): AutomationTask | null {
    const cancelled = this.queue.cancel(taskId)
    if (cancelled) automationJobRepository.upsertFromTask(cancelled)
    return cancelled
  }

  reschedule(taskId: string, priority?: AutomationPriority): AutomationTask | null {
    const task = this.queue.reschedule(taskId, priority)
    if (task) {
      automationJobRepository.upsertFromTask(task)
      void this.pump()
    }
    return task
  }

  listTasks(): AutomationTask[] {
    return this.queue.list()
  }

  listJobs(filters: AutomationFilters = {}): AutomationJob[] {
    return automationJobRepository.list(filters)
  }

  listSessions(): AutomationSession[] {
    const live = this.sessions.list()
    for (const session of live) automationSessionRepository.upsert(session)
    return automationSessionRepository.list()
  }

  listLogs(jobId?: string): AutomationLog[] {
    return automationLogRepository.list(jobId)
  }

  listEvents() {
    return automationEventBus.list()
  }

  listNotifications() {
    return [...this.notifications]
  }

  dashboard(): AutomationDashboard {
    const jobs = automationJobRepository.list()
    const queued = jobs.filter((j) => j.status === 'QUEUED' || j.status === 'RETRYING').length
    const running = jobs.filter((j) => j.status === 'RUNNING').length
    const completed = jobs.filter((j) => j.status === 'COMPLETED').length
    const failed = jobs.filter((j) => j.status === 'FAILED').length
    const durations = jobs
      .map((j) => j.executionTime)
      .filter((n): n is number => typeof n === 'number' && n >= 0)
    const averageDuration =
      durations.length === 0
        ? 0
        : Math.round((durations.reduce((a, b) => a + b, 0) / durations.length) * 100) / 100

    return { queued, running, completed, failed, averageDuration }
  }

  async pump(): Promise<void> {
    if (this.pumping) return
    this.pumping = true
    try {
      while (this.pool.canAccept()) {
        const task = this.queue.dequeue()
        if (!task) break
        void this.executeTask(task)
      }
    } finally {
      this.pumping = false
    }
  }

  async executeTask(task: AutomationTask): Promise<AutomationResult> {
    const startedAt = new Date().toISOString()
    task.status = 'RUNNING'
    task.startedAt = startedAt
    task.attempts += 1
    automationJobRepository.upsertFromTask(task)
    automationLogRepository.add(task.id, 'info', `Início ${task.operation}`)
    automationEventBus.emit('AutomationStarted', `Job ${task.id} iniciado`, {
      supplierId: task.supplier,
      jobId: task.id,
    })
    this.notify('start', `Início: ${task.supplier}/${task.operation}`)

    let session = this.sessions.getActiveForSupplier(task.supplier)
    if (!session) session = this.sessions.create(task.supplier)
    else session = this.sessions.touch(session.id)
    automationSessionRepository.upsert(session)

    const result = await this.pool.run(task, async (current, provider) => {
      await provider.initialize()
      const authOk = await provider.authenticate()
      if (!authOk) {
        return {
          success: false,
          executionTime: 0,
          itemsFound: 0,
          warnings: [],
          errors: ['Falha de autenticação (stub)'],
          logs: ['authenticate:failed'],
        }
      }
      await provider.open()
      let items: unknown[] = []
      if (current.operation === 'search') {
        items = await provider.search(String(current.payload?.term ?? 'mock'))
      } else if (current.operation === 'extract') {
        items = await provider.extract(String(current.payload?.selector ?? ''))
      } else if (current.operation === 'healthCheck') {
        items = [await provider.healthCheck()]
      } else {
        await provider.navigate(String(current.payload?.path ?? '/'))
      }
      await provider.close()
      return {
        success: true,
        executionTime: 0,
        itemsFound: items.length,
        warnings: [],
        errors: [],
        logs: [`operation:${current.operation}`, `items:${items.length}`],
        data: items,
      }
    }, (code) => this.ensureProvider(code))

    const finishedAt = new Date().toISOString()
    const duration =
      result.executionTime ||
      Math.max(0, new Date(finishedAt).getTime() - new Date(startedAt).getTime())

    if (result.success) {
      task.status = 'COMPLETED'
      task.finishedAt = finishedAt
      task.duration = duration
      task.error = null
      automationJobRepository.upsertFromTask(task)
      automationLogRepository.add(task.id, 'info', `Concluído em ${duration}ms`)
      automationEventBus.emit('AutomationFinished', `Job ${task.id} concluído`, {
        supplierId: task.supplier,
        jobId: task.id,
      })
      this.notify('success', `Conclusão: ${task.supplier}/${task.operation}`)
    } else {
      const canRetry = task.attempts < task.maxAttempts
      task.error = result.errors[0] ?? 'Falha'
      task.duration = duration
      if (canRetry) {
        task.status = 'RETRYING'
        task.finishedAt = null
        automationJobRepository.upsertFromTask(task)
        automationLogRepository.add(
          task.id,
          'warn',
          `Tentativa ${task.attempts}/${task.maxAttempts} falhou — reagendando`,
        )
        await delay(this.config.retryBaseDelayMs * 2 ** (task.attempts - 1))
        this.queue.requeueForRetry(task)
      } else {
        task.status = 'FAILED'
        task.finishedAt = finishedAt
        automationJobRepository.upsertFromTask(task)
        automationLogRepository.add(task.id, 'error', task.error)
        automationEventBus.emit('AutomationFailed', `Job ${task.id} falhou`, {
          supplierId: task.supplier,
          jobId: task.id,
        })
        this.notify('error', `Erro: ${task.supplier}/${task.operation}`)
      }
    }

    void this.pump()
    return { ...result, executionTime: duration }
  }

  releaseResources(supplierCode?: string): void {
    if (supplierCode) {
      for (const session of this.sessions.list('ACTIVE')) {
        if (session.supplierId === supplierCode) this.sessions.close(session.id)
      }
      return
    }
    for (const session of this.sessions.list('ACTIVE')) this.sessions.close(session.id)
  }

  resetMemory(): void {
    this.queue.clear()
    this.sessions.reset()
    this.providers.clear()
    this.notifications.length = 0
    automationEventBus.clear()
    automationJobRepository.reset()
    automationLogRepository.reset()
    automationSessionRepository.reset()
  }

  private notify(type: string, message: string): void {
    this.notifications.unshift({
      id: crypto.randomUUID(),
      type,
      message,
      at: new Date().toISOString(),
    })
    if (this.notifications.length > 100) this.notifications.pop()
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const automationManager = new AutomationManager()
